import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { GlassWater, Plus } from "lucide-react";
import { PageHeader } from "./Sugar";

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function Water() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(todayStr);
  const goal = 2500;

  const load = async () => { try { const { data } = await api.get("/water"); setLogs(data); } catch {} };
  useEffect(() => { load(); }, []);

  const dayMl = useMemo(
    () => logs.filter((l) => l.date === date).reduce((a, l) => a + (l.amount_ml || 0), 0),
    [logs, date]
  );
  const pct = Math.min(100, Math.round((dayMl / goal) * 100));

  const add = async (ml) => {
    setLoading(true);
    try { await api.post("/water", { amount_ml: ml, date }); await load(); } finally { setLoading(false); }
  };

  const isToday = date === todayStr();
  const dayLogs = logs.filter((l) => l.date === date);

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto" data-testid="water-page">
      <PageHeader icon={GlassWater} title="Water Intake" subtitle="Stay hydrated throughout the day" accent="blue" />

      <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-6 flex items-center justify-between gap-4 flex-wrap" data-testid="water-date-bar">
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Logging for</Label>
          <Input type="date" value={date} max={todayStr()} onChange={(e) => setDate(e.target.value)} className="mt-1 rounded-xl bg-slate-50 w-44" data-testid="water-date" />
        </div>
        <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200">{isToday ? "Today" : new Date(date).toLocaleDateString()}</span>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6 flex items-center gap-6" data-testid="water-progress">
        <WaterRing pct={pct} />
        <div>
          <div className="text-xs uppercase font-bold tracking-wider text-slate-400">{isToday ? "Today" : "Selected day"}</div>
          <div className="text-3xl font-bold text-slate-900">{(dayMl / 1000).toFixed(1)}<span className="text-slate-400 text-base font-medium ml-1">L</span></div>
          <div className="text-sm text-slate-500">Goal {(goal / 1000).toFixed(1)} L • {pct}% complete</div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-6">
        <h3 className="font-heading font-semibold text-slate-800 mb-3">Quick add</h3>
        <div className="grid grid-cols-3 gap-3">
          {[250, 500, 1000].map(ml => (
            <Button key={ml} disabled={loading} onClick={() => add(ml)} data-testid={`water-add-${ml}`} className="rounded-full h-12 bg-blue-600 hover:bg-blue-700 text-white">
              <Plus className="w-4 h-4 mr-1" /> {ml >= 1000 ? "1 L" : `${ml} ml`}
            </Button>
          ))}
        </div>
      </div>

      <h3 className="font-heading font-semibold text-slate-800 mb-3">{isToday ? "Today's log" : "Log for this date"}</h3>
      <div className="space-y-2" data-testid="water-list">
        {dayLogs.length === 0 && <div className="text-sm text-slate-400">No water logged for this date.</div>}
        {dayLogs.map(l => (
          <div key={l.id} className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-900">{l.amount_ml >= 1000 ? `${(l.amount_ml/1000).toFixed(1)} L` : `${l.amount_ml} ml`}</div>
            <div className="text-xs text-slate-500">{new Date(l.logged_at || l.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function WaterRing({ pct }) {
  const radius = 46; const c = 2 * Math.PI * radius;
  const offset = c - (pct / 100) * c;
  return (
    <svg width="120" height="120" viewBox="0 0 120 120">
      <circle cx="60" cy="60" r={radius} stroke="#e2e8f0" strokeWidth="10" fill="none" />
      <circle cx="60" cy="60" r={radius} stroke="#2563eb" strokeWidth="10" fill="none"
        strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round"
        transform="rotate(-90 60 60)" style={{ transition: "stroke-dashoffset 800ms ease" }} />
      <text x="60" y="65" textAnchor="middle" fontSize="20" fontWeight="700" fill="#0f172a">{pct}%</text>
    </svg>
  );
}
